'use client';

import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from './atoms/button';
import { TextField } from './atoms/text-field';

const otpSchema = z.object({
  code: z
    .string()
    .min(6, 'OTP code must be 6 digits')
    .max(6, 'OTP code must be 6 digits'),
});

type OtpFormData = z.infer<typeof otpSchema>;

interface OtpCodeFormProps {
  phoneNumber: string;
  onSubmit: (code: string) => Promise<void>;
  onBack: () => void;
  loading: boolean;
  error: string | null;
  language: 'en' | 'de';
}

export const OtpCodeForm: React.FC<OtpCodeFormProps> = (props) => {
  const { phoneNumber, onSubmit, onBack, loading, error, language } = props;
  const form = useForm<OtpFormData>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      code: '',
    },
  });

  const handleSubmit = async (data: OtpFormData) => {
    await onSubmit(data.code);
  };

  // Translations
  const translations = {
    en: {
      otpTitle: 'Enter Verification Code',
      otpDescription: 'We sent a 6-digit code to',
      otpLabel: 'Verification Code',
      otpPlaceholder: '123456',
      verifyButton: 'Verify Code',
      verifyingButton: 'Verifying...',
      backButton: 'Use a different phone number',
    },
    de: {
      otpTitle: 'Bestätigungscode eingeben',
      otpDescription: 'Wir haben einen 6-stelligen Code an',
      otpLabel: 'Bestätigungscode',
      otpPlaceholder: '123456',
      verifyButton: 'Code bestätigen',
      verifyingButton: 'Wird bestätigt...',
      backButton: 'Eine andere Telefonnummer verwenden',
    },
  };

  const t = translations[language];

  return (
    <>
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-bold tracking-tight">{t.otpTitle}</h1>
        <p className="text-white/70">{`${t.otpDescription} ${phoneNumber}`}</p>
      </div>

      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Controller
          name="code"
          control={form.control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder={t.otpPlaceholder}
              label={t.otpLabel}
              error={form.formState.errors.code?.message}
              maxLength={6}
              className="text-center text-2xl tracking-widest"
              value={field.value || ''}
            />
          )}
        />

        {error && (
          <div className="text-center text-sm text-red-500">{error}</div>
        )}

        <div className="space-y-3">
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-white text-black hover:bg-white/90 disabled:opacity-50"
          >
            <span> {loading ? t.verifyingButton : t.verifyButton}</span>
          </Button>

          <button
            type="button"
            onClick={onBack}
            className="w-full text-sm text-white/70 hover:text-white"
          >
            {t.backButton}
          </button>
        </div>
      </form>
    </>
  );
};
